// Web Crawl Progress - Shows real-time crawl progress with polling
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, CheckCircle, XCircle, Globe } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { webSourcesApi } from '@/lib/api/client'

interface CrawlProgress {
  pagesFound: number
  pagesCrawled: number
  currentUrl?: string
  errors: Array<{ url: string; error: string }>
}

interface WebSourceStatus {
  id: string
  crawlStatus: 'pending' | 'fetching' | 'extracting' | 'completed' | 'error'
  crawlProgress: CrawlProgress | null
  errorMessage: string | null
  title: string | null
  sourceId: string | null
}

interface WebCrawlProgressProps {
  webSourceId: string
  accountId: string
  projectId: string
  onComplete?: (sourceId: string | null) => void
}

export function WebCrawlProgress({ webSourceId, accountId, projectId, onComplete }: WebCrawlProgressProps) {
  const { data: status, isLoading } = useQuery<WebSourceStatus>({
    queryKey: ['web-source-status', webSourceId],
    queryFn: () => webSourcesApi.getStatus(accountId, projectId, webSourceId),
    refetchInterval: (query) => {
      const currentStatus = query.state.data?.crawlStatus
      if (currentStatus === 'completed' || currentStatus === 'error') {
        return false
      }
      return 2000 // Poll every 2 seconds
    },
  })

  // Call onComplete when crawl finishes
  useEffect(() => {
    if (status?.crawlStatus === 'completed' || status?.crawlStatus === 'error') {
      onComplete?.(status.sourceId)
    }
  }, [status?.crawlStatus, status?.sourceId, onComplete])

  if (isLoading || !status) {
    return (
      <div className="flex items-center gap-2 p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading status...</span>
      </div>
    )
  }

  const progress = status.crawlProgress
  const percentage =
    progress?.pagesFound && progress.pagesFound > 0
      ? Math.round((progress.pagesCrawled / progress.pagesFound) * 100)
      : 0

  const getStatusDisplay = () => {
    switch (status.crawlStatus) {
      case 'completed':
        return { icon: <CheckCircle className="h-5 w-5 text-green-500" />, text: 'Crawl Complete' }
      case 'error':
        return { icon: <XCircle className="h-5 w-5 text-red-500" />, text: 'Crawl Failed' }
      case 'extracting':
        return { icon: <Loader2 className="h-5 w-5 animate-spin text-business" />, text: 'Extracting Entities...' }
      case 'fetching':
        return { icon: <Loader2 className="h-5 w-5 animate-spin text-business" />, text: 'Crawling Website...' }
      case 'pending':
      default:
        return { icon: <Loader2 className="h-5 w-5 animate-spin text-business" />, text: 'Starting...' }
    }
  }

  const statusDisplay = getStatusDisplay()

  return (
    <div className="space-y-3 p-4 bg-warm-50 rounded-lg">
      <div className="flex items-center gap-2">
        {statusDisplay.icon}
        <span className="font-medium">{statusDisplay.text}</span>
      </div>

      {progress && status.crawlStatus !== 'completed' && status.crawlStatus !== 'error' && (
        <>
          <Progress value={percentage} className="h-2" />
          <div className="flex justify-between text-xs text-text-secondary">
            <span>
              {progress.pagesCrawled} / {progress.pagesFound} pages
            </span>
            <span>{percentage}%</span>
          </div>
          {progress.currentUrl && (
            <p className="text-xs text-text-tertiary truncate flex items-center gap-1">
              <Globe className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{progress.currentUrl}</span>
            </p>
          )}
        </>
      )}

      {status.crawlStatus === 'completed' && progress && (
        <p className="text-sm text-text-secondary">
          Successfully crawled {progress.pagesCrawled} page{progress.pagesCrawled !== 1 ? 's' : ''}
          {status.title && <span className="font-medium"> from {status.title}</span>}
        </p>
      )}

      {status.crawlStatus === 'error' && status.errorMessage && (
        <p className="text-sm text-red-600">{status.errorMessage}</p>
      )}

      {progress?.errors && progress.errors.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-text-secondary hover:text-text">
            {progress.errors.length} page{progress.errors.length !== 1 ? 's' : ''} failed
          </summary>
          <ul className="mt-1 space-y-1 pl-4">
            {progress.errors.slice(0, 5).map((err, i) => (
              <li key={i} className="text-red-600 truncate">
                {err.url}: {err.error}
              </li>
            ))}
            {progress.errors.length > 5 && (
              <li className="text-text-tertiary">...and {progress.errors.length - 5} more</li>
            )}
          </ul>
        </details>
      )}
    </div>
  )
}
